/**
 * event-optional.js
 */

import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
  useMediaQuery,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { shouldRenderMobileLayout } from 'utils/app-context';
import { EditorIconSection } from 'components/icon-section/icon-section';
import EditorAccordion from 'components/menu-drawer/editor-accordion';
import EventEditDescription from './event-edit-description';
import EventEditTags from './event-edit-tags';
import FormGridContainer from 'components/menu-drawer/form-grid-container';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import FormSectionTitle from 'components/menu-drawer/form-section-title';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import TextField from 'components/form-inputs/text-field';
import Icon from 'components/icon';


const useStyles = makeStyles(theme => ({
  flexColumn: {
    flexDirection: 'column',
  },
}));


export default function EventEditOptional(props) {
  const {
    options,
    drawerState,
    translation,
  } = props;
  const classes = useStyles();
  const isMobile = useMediaQuery(theme => theme.size.menuDrawer.mediaRuleMobileContent, { noSsr: true });
  const { t } = useTranslation(translation);


  return (
    <EditorIconSection
      iconName='settings'
      iconMarginTop={4}
      helpProps={{
        translation,
        section: 'optional'
      }}
    >
      <EditorAccordion
        // highlight
        name={t('optional.sectionName')}
        data-testid='event-edit-optional-accordion'
        contentClassName={classes.flexColumn}

        summary={<>
          <FormSectionTitle>
            {window.innerWidth < 450 ? 'Optional Fields' : 'OPTIONAL: Tags, Comments, Venue Contact'}
          </FormSectionTitle>
          <Typography
            sx={{
              color: 'sys.onSurfaceVariant',
              fontSize: 'sizes.1'
            }}
          >
            <Icon
              type='tip'
              fontSize='tiny'
              sx={{ color: 'comp.tip.icon', mr: 0.5 }}
            />
            {isMobile
              ? <>TIP: Record demos with a Demonstration tag</>
              : <>TIP: Record your public demos by using a <strong>Demonstration</strong> tag</>}
          </Typography>
        </>}

        detail={<>

          {/* name - mobile-only */}
          {shouldRenderMobileLayout() &&
          <HelpTextItem
            sidebarText={drawerState.sidebarText}
            title={t('optional.name.helpTitle')}
            text={t('optional.name.helpInfo')}
          >
            {focusHandlers =>
              <FormGridContainer>
                <FormGridItem md={7}>
                  <TextField
                    name='name'
                    label={t('optional.name.label')}
                    focusHandlers={focusHandlers}
                    rules={{
                      required: t('optional.name.errMsgEmpty'),
                      validate: value => value.trim() !== '' || t('optional.name.errMsgEmpty'),
                      maxLength: {
                        value: 150,
                        message: t('optional.name.errMsgLength')
                      }
                    }}
                  />
                </FormGridItem>
              </FormGridContainer>}
          </HelpTextItem>}

          <HelpTextItem
            sidebarText={drawerState.sidebarText}
            title={t('optional.venueContact.helpTitle')}
            text={t('optional.venueContact.helpInfo')}
          >
            {focusHandlers =>
              <FormGridContainer>
                <FormGridItem md={7}>
                  <TextField
                    name='venueContact'
                    label={t('optional.venueContact.label')}
                    focusHandlers={focusHandlers}
                    rules={{
                      maxLength: {
                        value: 150,
                        message: t('optional.venueContact.errMsgLength')
                      }
                    }}
                  />
                </FormGridItem>
              </FormGridContainer>}
          </HelpTextItem>

          {/* tags */}
          <EventEditTags
            {...props}
            options={options.tags}
          />

          {/* description */}
          <EventEditDescription
            {...props}
          />

        </>}
      />
    </EditorIconSection>
  );
}